/**
 * waMessageTime — how the omnichannel components read a message timestamp.
 *
 * WHY THIS EXISTS
 * The WhatsApp message store holds two live encodings of one field, and
 * before this module each omnichannel surface read one of them and got
 * the other wrong:
 *
 *   • epoch seconds as a number — the inline webhook and every send path
 *     (`routers/infobip.py:610`, 144,077 entries);
 *   • an ISO-8601 string carrying the basic offset form `+0000` — the
 *     outbox inbound path, which stores Infobip's `receivedAt` verbatim
 *     (`services/whatsapp_inbound_shape.py:31-42`). Sole producer of
 *     inbound messages since 2026-08-31 04:57 UTC.
 *
 * `RecordWhatsAppChat` and `AgentStatusManager` each carried a
 * character-identical `toMilliseconds` that did `parseInt(ts)` on a
 * string. `parseInt("2026-09-01T12:08:35.000+0000")` stops at the first
 * non-digit and returns `2026`, which is below the seconds/ms threshold,
 * so it became 2,026,000 ms — 1 January 1970. Every inbound message of
 * the last two days was filed under a `יום חמישי, 1 בינואר` date header
 * above `אתמול` and `היום`, out of the agent's view: 4,905 messages
 * across 797 conversations, 283 of them media. They were displaced, not
 * dropped. See `docs/design/wa-extraction/75-record-tab-message-gap.md`.
 *
 * `WhatsAppChat` had the *inverse* bug — bare `new Date(ts)` with no
 * numeric branch, so `new Date(1788264491)` is 21 January 1970 and the
 * communication centre mis-rendered the other half of the store.
 *
 * ONE READER, NO SIXTH THRESHOLD
 * The seconds-versus-milliseconds threshold and the offset-less-string
 * rule live in exactly one place, `workspace/utils/waTime.js` (PR #1640),
 * and this module delegates to it rather than restating either. There
 * were already five copies of that threshold; this module exists so that
 * the omnichannel tree contributes none. It adds only the Hebrew
 * presentation the three components share, which is why it sits here and
 * not inside `waTime` — that module is deliberately import-free so the
 * timezone matrix in `sort-recent-timestamp.test.js` can drive it in a
 * child process with `TZ` set.
 *
 * THE NAIVE CASE MATTERS HERE
 * `chats.json`'s `lastMessageTime` is written by `datetime.now().isoformat()`
 * on the backend host and is naive **host-local**, not UTC —
 * `WhatsAppChat`'s chat-list clock reads it. `toEpochMs` resolves an
 * offset-less string against `SERVER_WALL_CLOCK_TZ`, which inverts the
 * writer. Reading it as UTC would trade this loud failure for a quiet
 * two-hour one; `waTime`'s header sets that out in full.
 *
 * AN UNREADABLE TIMESTAMP IS NOT A TIME
 * `toEpochMs` returns `null` for anything missing or unparseable, and
 * this module keeps it `null` rather than substituting one. The old
 * helper returned `Date.now()` for a falsy timestamp, which filed an
 * undated message under `היום` — the freshest position in the thread —
 * on no evidence, and `NaN` for junk, which rendered the literal string
 * `Invalid Date` as a date header. Undated messages now group under
 * `NO_DATE_GROUP` and carry no time label, so they land neither at the
 * top nor at the bottom by accident.
 */

import { toEpochMs, compareChatRecency } from '../../../workspace/utils/waTime';

// `compareChatRecency` is the canonical most-recent-first comparator for
// chat rows: it reads `lastMessageTime` (falling back to `timestamp`)
// through `toEpochMs`, sorts an unreadable timestamp last rather than
// treating it as recent, and returns 0 for equal instants so a stable
// sort leaves ties alone. Re-exported so the omnichannel components have
// one import surface rather than two.
export { toEpochMs, compareChatRecency };

/** Date header for messages whose timestamp cannot be read at all. */
export const NO_DATE_GROUP = 'ללא תאריך';

/**
 * The date header a message belongs under: `היום`, `אתמול`, or the long
 * Hebrew date. Day boundaries are the *browser's*, which is the agent's
 * own day and the right frame for a header they read; only the instant
 * itself comes from the store.
 *
 * `now` is injectable so tests can pin "today" instead of racing it.
 */
export function dayGroupKey(value, now = Date.now()) {
  const ms = toEpochMs(value);
  if (ms == null) return NO_DATE_GROUP;

  const date = new Date(ms);
  const today = new Date(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'היום';
  if (date.toDateString() === yesterday.toDateString()) return 'אתמול';
  return date.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** `HH:MM` in Hebrew locale, or `''` when there is no readable time. */
export function formatTimeHe(value) {
  const ms = toEpochMs(value);
  if (ms == null) return '';
  return new Date(ms).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
}

/** `toLocaleString('he-IL', opts)`, or `''` when there is no readable time. */
export function formatDateTimeHe(value, opts) {
  const ms = toEpochMs(value);
  if (ms == null) return '';
  return new Date(ms).toLocaleString('he-IL', opts);
}

/**
 * Hours elapsed since `value`, or `null` when it cannot be read.
 * `null` is not "long ago": the 24-hour window caller must decide what
 * to do with an unknown, rather than inherit a fabricated answer.
 */
export function hoursSince(value, now = Date.now()) {
  const ms = toEpochMs(value);
  if (ms == null) return null;
  return (now - ms) / (1000 * 60 * 60);
}

/**
 * Group messages under date headers, preserving arrival order.
 *
 * The returned object's keys are Hebrew strings, so `Object.entries`
 * yields them in **insertion** order — the order the first message of
 * each day arrived in. The backend already sorts the thread
 * (`routers/whatsapp.py` `_msg_sort_key`, which reads both encodings),
 * so insertion order is chronological and the headers come out in
 * chronological order too. That is precisely what the 1970 bug broke:
 * the displaced group was created at the first ISO message and every
 * later one was appended to it, so it sat wherever that first message
 * happened to fall — above `אתמול` and `היום`, off the agent's screen.
 *
 * Lives here rather than inside the component so the grouping under test
 * is the grouping that ships.
 */
export function groupMessagesByDate(msgs, now = Date.now()) {
  const groups = {};
  (msgs || []).forEach((msg) => {
    const dateKey = dayGroupKey(msg && msg.timestamp, now);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
  });
  return groups;
}
